import React, { useState, ChangeEvent } from 'react';
import { useAPI } from 'utils/api';
import { theme } from 'styles/theme';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useGlobal } from 'context/global/GlobalContext';
import { showToast } from 'components/atomic/Toast/Toast';
import { useSuccessDrawerContext } from 'context/SuccessDrawerContext/SuccessDrawerContext';
import { useThemeContext } from 'context/ThemeContext/ThemeContext';
import validateNewDtcVisitor from 'utils/validateNewDtcVisitor';
import PageWrapper from 'components/layout/PageWrapper';
import TextArea from 'components/atomic/TextArea';
import getRootDomain from 'utils/getRootDomain';
import Scrollbar from 'utils/Scrollbar';
import Button from 'components/atomic/Button';
import Input from 'components/atomic/Input';

const LostAndFoundDrawer: React.FC = () => {
  const [finderMessage, setFinderMessage] = useState<string>('');
  const [finderEmail, setFinderEmail] = useState<string>('');
  const [finderPhone, setFinderPhone] = useState<string>('');
  const [finderName, setFinderName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { openDrawer, setMeta, showSuccess } = useSuccessDrawerContext();
  const { productDetails, logEvent } = useGlobal();
  const { brandTheme } = useThemeContext();
  const { t } = useTranslation('translation', {
    keyPrefix: 'lostAndFound',
  });

  const handleFormReset = () => {
    setFinderMessage('');
    setFinderEmail('');
    setFinderPhone('');
    setFinderName('');
  };

  const endpoint = `lostAndFound?finderMessage=${encodeURIComponent(
    finderMessage
  )}&finderEmail=${encodeURIComponent(
    finderEmail
  )}&finderPhone=${encodeURIComponent(
    finderPhone
  )}&finderName=${encodeURIComponent(
    finderName
  )}&brandName=${encodeURIComponent(
    productDetails?.brand.name || ''
  )}&experienceTitle=${encodeURIComponent(
    productDetails?.product.name || ''
  )}&tagId=${encodeURIComponent(
    productDetails?.tag?.id || ''
  )}&url=${encodeURIComponent(
    (() => {
      const rootDomain = getRootDomain(window?.location?.href);
      const productSlug = productDetails?.tag?.slug;
      if (!rootDomain || !productSlug) return '';
      return `https://${rootDomain}/c/${productSlug}`;
    })()
  )}&themeColor=${encodeURIComponent(brandTheme || theme.primary)}`;

  const [sendMessage] = useAPI({
    endpoint,
    method: 'POST',
    onSuccess: (response) => {
      if (response.success) {
        setMeta({
          title: t('successMessage'),
          description: t('successMessageDescription'),
        });
        openDrawer();
        showSuccess();
        setLoading(false);
        handleFormReset();
        logEvent({
          eventType: 'ENGAGEMENTS',
          event: 'ITEM_FOUND',
          customerNewDtcVisitor: validateNewDtcVisitor(
            productDetails?.brand?.website,
            productDetails?.brand?.shopifyStoreName
          ),
          data: { finderMessage, finderEmail, finderPhone, finderName },
        });
      } else {
        showToast({
          message: t('errorSendingMessage'),
          type: 'error',
        });
        setLoading(false);
      }
    },
    onError: () => {
      showToast({
        message: t('errorSendingMessage'),
        type: 'error',
      });
      setLoading(false);
    },
  });

  return (
    <PageWrapper
      pageTitle={t('pageHeaderTitle')}
      allowGoBack={productDetails ? true : false}
    >
      <Helmet>{t('pageTitle')}</Helmet>
      <Scrollbar style={{ background: '#FFFFFF' }}>
        <form
          className='relative w-full flex flex-col px-5 pt-3 pb-8 gap-4 overflow-x-hidden overflow-y-auto'
          onSubmit={(event) => {
            event.preventDefault();
            setLoading(true);
            sendMessage(undefined, null, true);
          }}
        >
          <TextArea
            name='finder-message'
            value={finderMessage}
            onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
              setFinderMessage(event.target.value)
            }
            placeholder={
              finderMessage
                ? t('messageTextboxFilledPlaceholder')
                : t('messageTextboxPlaceholder')
            }
            focusedPlaceholder={t('messageTextboxFilledPlaceholder')}
          />
          <Input
            type='text'
            name='finder-name'
            value={finderName}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setFinderName(event.target.value)
            }
            placeholder={
              finderName
                ? t('nameInputFilledPlaceholder')
                : t('nameInputPlaceholder')
            }
          />
          <Input
            type='email'
            name='finder-email'
            value={finderEmail}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setFinderEmail(event.target.value)
            }
            placeholder={
              finderEmail
                ? t('emailInputFilledPlaceholder')
                : t('emailInputPlaceholder')
            }
          />
          <Input
            type='number'
            name='finder-phone'
            value={finderPhone}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setFinderPhone(event.target.value)
            }
            placeholder={
              finderPhone
                ? t('phoneInputFilledPlaceholder')
                : t('phoneInputPlaceholder')
            }
          />
          <Button
            type='submit'
            variant='dark'
            loading={loading}
            title={t('sendMessageButton')}
            disabled={!finderMessage.trim()}
          />
          <span className='block text-center text-xs text-primary font-medium'>
            {t('areYouOwnerText')}{' '}
            <Link
              to='/app/login'
              className='text-primary font-semibold underline tracking-[0.3px] cursor-pointer'
            >
              {t('signInLinkText')}
            </Link>
          </span>
        </form>
      </Scrollbar>
    </PageWrapper>
  );
};

export default LostAndFoundDrawer;
